import React, { useCallback, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Icon from '../components/Icon';
import { useAuth } from '../context/AuthContext';
import AuthScreen from '../components/AuthScreen';
import api from '../lib/api';
import { logError } from '../lib/log';
import { usePageMeta } from '../hooks/usePageMeta';
import WellbeingProfile from '../components/dashboard/WellbeingProfile';
import AccountSettings from '../components/dashboard/AccountSettings';
import WellbeingSpaceTop from '../components/dashboard/WellbeingSpaceTop';

const HERO_IMG = 'https://lh3.googleusercontent.com/aida-public/AB6AXuDp6aXE1NM4HErOsVemu6-hKdyMjPXeb4bSprNg2bnzv1ftLZhDnoLWg00zBlp_l2yy8CrMN36m92jn7CWgyNUk3tm2a2NFoPdNre1qSvaSsRjdz0f3jOJQqHqnDE_xiypiCf6F7eIGWXbUYl58ljXQ0FrsH9DUKI6PsaqSTvvi3DKyxIo4Cn8cxUVJ4y2EDkihfSlyrP4BD9UMgYfMG3NmH0Ok94TXVRI96ig2bgz3-V2pgKhPky0';

export default function MyLogin() {
  usePageMeta({
    title: 'My Login | OVERALL WELLBEING',
    description: 'Sign in to your private OVERALL WELLBEING space to track habits, saved topics, health metrics and goals.',
    path: '/my-login',
    noindex: true,
  });
  const { user } = useAuth();
  const [reflection, setReflection] = useState('');
  const [saved, setSaved] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [savingReflection, setSavingReflection] = useState(false);
  const [habits, setHabits] = useState([]);
  const [savedTopics, setSavedTopics] = useState([]);
  const [priorities, setPriorities] = useState([]);
  const [fprogress, setFprogress] = useState([]);
  const [streak, setStreak] = useState(null);
  const [progressByHabit, setProgressByHabit] = useState({}); // habit_id -> today done bool
  const [editingPriorities, setEditingPriorities] = useState(false);
  const [prioritySel, setPrioritySel] = useState([]); // array of foundation codes
  const [notice, setNotice] = useState('');
  const today = new Date().toISOString().slice(0, 10);

  const isAuthed = user && typeof user === 'object';

  const loadDashboard = useCallback(async () => {
    try {
      const [h, s, p, fp, st] = await Promise.all([
        api.get('/me/habits'),
        api.get('/me/saved'),
        api.get('/me/priorities'),
        api.get('/me/foundation-progress'),
        api.get('/me/streak'),
      ]);
      setHabits(h.data || []);
      setSavedTopics((s.data || []).filter((x) => x.content_type === 'topic'));
      const items = (p.data && p.data.items) || [];
      setPriorities(items);
      setPrioritySel(items.map((it) => it.foundation_code).filter(Boolean));
      setFprogress((fp.data && fp.data.foundations) || []);
      setStreak(st.data || null);
      const done = {};
      await Promise.all((h.data || []).map(async (hb) => {
        try {
          const { data } = await api.get(`/me/habits/${hb.id}/progress`);
          done[hb.id] = (data || []).some((pr) => pr.date === today && pr.status === 'done');
        } catch (e) { logError('MyLogin.habitProgress', e); }
      }));
      setProgressByHabit(done);
    } catch (e) {
      logError('MyLogin.loadDashboard', e);
      setNotice('Something went wrong. Please try again.');
    }
  }, [today]);

  const logHabit = async (habitId) => {
    try {
      await api.post(`/me/habits/${habitId}/progress`, { date: today, status: 'done' });
      setProgressByHabit((m) => ({ ...m, [habitId]: true }));
      try { const { data } = await api.get('/me/streak'); setStreak(data || null); } catch (e) { logError('MyLogin.streak', e); }
    } catch (e) {
      logError('MyLogin.logHabit', e);
      setNotice('Something went wrong. Please try again.');
    }
  };

  const removeSaved = async (id) => {
    try {
      await api.delete(`/me/saved/${id}`);
      setSavedTopics((list) => list.filter((s) => s.id !== id));
    } catch (e) {
      logError('MyLogin.removeSaved', e);
      setNotice('Something went wrong. Please try again.');
    }
  };

  const removeHabit = async (id) => {
    try {
      await api.delete(`/me/habits/${id}`);
      setHabits((list) => list.filter((h) => h.id !== id));
    } catch (e) {
      logError('MyLogin.removeHabit', e);
      setNotice('Something went wrong. Please try again.');
    }
  };

  const togglePriority = (code) => {
    setPrioritySel((sel) => {
      if (sel.includes(code)) return sel.filter((c) => c !== code);
      if (sel.length >= 3) return sel; // gentle cap of 3 focuses
      return [...sel, code];
    });
  };

  const savePriorities = async () => {
    const nameByCode = {};
    fprogress.forEach((f) => { nameByCode[f.code] = f.name; });
    const items = prioritySel.map((code, i) => ({ foundation_code: code, rank: i + 1, label: nameByCode[code] || code }));
    try {
      await api.put('/me/priorities', { items });
      setPriorities(items);
      setEditingPriorities(false);
    } catch (e) {
      logError('MyLogin.savePriorities', e);
      setNotice('Something went wrong. Please try again.');
    }
  };

  useEffect(() => {
    if (!isAuthed) return;
    let active = true;
    (async () => {
      try {
        const { data } = await api.get('/me/reflections');
        if (active && Array.isArray(data) && data.length > 0) {
          setReflection(data[0].body || '');
          setLastSaved(data[0].created_at || null);
        }
      } catch (e) {
        logError('MyLogin.reflections', e);
      }
      if (active) loadDashboard();
    })();
    return () => {
      active = false;
    };
  }, [isAuthed, loadDashboard]);

  const saveReflection = async () => {
    if (!reflection.trim()) return;
    setSavingReflection(true);
    try {
      const { data } = await api.post('/me/reflections', {
        body: reflection.trim(),
        prompt: 'The Saturday Compass',
      });
      setSaved(true);
      setLastSaved(data.created_at || new Date().toISOString());
    } catch (e) {
      logError('MyLogin.saveReflection', e);
      setNotice('Something went wrong. Please try again.');
    } finally {
      setSavingReflection(false);
    }
  };

  const formatSaved = (iso) => {
    if (!iso) return 'No reflection saved yet';
    try {
      const d = new Date(iso);
      return `Last saved: ${d.toLocaleString()}`;
    } catch (e) {
      logError('MyLogin.formatSaved', e);
      return 'Saved';
    }
  };

  // Session still being verified.
  if (user === null) {
    return (
      <div className="w-full flex items-center justify-center py-space-3xl" data-testid="auth-loading">
        <span className="inline-block w-2 h-2 rounded-full bg-surface-tint animate-pulse" />
        <span className="ml-space-sm font-label-md text-label-md uppercase tracking-wider text-on-surface-variant">
          Preparing your space…
        </span>
      </div>
    );
  }

  // Not authenticated — show sign in / sign up gate.
  if (!isAuthed) {
    return <AuthScreen />;
  }

  const firstName = (user.name || 'there').split(' ')[0];
  const hour = new Date().getHours();
  const greeting = hour < 12 ? 'Good morning' : hour < 18 ? 'Good afternoon' : 'Good evening';
  const nextHabit = habits.find((h) => !progressByHabit[h.id]) || habits[0] || null;
  const nextHabitDone = nextHabit ? !!progressByHabit[nextHabit.id] : false;

  return (
    <div className="flex flex-col w-full" data-testid="dashboard">
      {/* Status horizon */}
      <div className="w-full px-margin-mobile lg:px-margin pt-space-xl pb-space-lg">
        <div className="flex flex-wrap items-center justify-between gap-space-md pb-space-lg border-b border-surface-container">
          <div className="flex items-center gap-space-sm">
            <span className="inline-block w-2 h-2 rounded-full bg-surface-tint shadow-[0_0_10px_rgba(240,40,223,0.7)] animate-pulse" />
            <span className="font-label-md text-label-md uppercase tracking-wider text-on-surface-variant">Your private wellbeing space</span>
          </div>
          <div className="flex items-center gap-space-sm font-label-md text-label-md text-on-surface-variant uppercase tracking-wider">
            <span data-testid="dashboard-date">{new Date().toLocaleDateString(undefined, { weekday: 'long', day: 'numeric', month: 'long' })}</span>
            <span className="text-surface-container-highest">/</span>
            <span className="text-primary font-bold">Only you can see this</span>
          </div>
        </div>
      </div>

      {notice ? (
        <div className="w-full px-margin-mobile lg:px-margin pb-space-md">
          <p className="font-body-md text-body-md text-on-surface-variant" data-testid="dashboard-notice">{notice}</p>
        </div>
      ) : null}

      {/* Greeting */}
      <section className="w-full px-margin-mobile lg:px-margin pb-space-2xl">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-gutter items-end">
          <div className="lg:col-span-8 flex flex-col">
            <span className="font-label-md text-label-md uppercase text-primary tracking-widest mb-space-xs font-semibold" data-testid="dashboard-greeting">{greeting}, {firstName}</span>
            <h1 className="font-headline-lg text-headline-lg uppercase tracking-tight text-outline leading-snug lg:text-display-hero lg:leading-tight">
              What Matters <br />
              <span className="text-surface-tint">Right Now</span>
            </h1>
            <p className="font-body-lg text-body-lg text-on-surface-variant mt-space-md max-w-2xl">Here is your calm personal anchor for today. You don’t need to work on everything — focus on the two intentions you’ve selected for this week.</p>
          </div>
          <div className="lg:col-span-4 flex flex-col lg:items-end justify-between self-stretch pt-space-md lg:pt-0">
            <div className="flex flex-col gap-space-xs w-full lg:max-w-xs" data-testid="dashboard-priorities">
              <span className="font-label-sm text-label-sm uppercase tracking-wider text-on-surface-variant mb-space-xs">This week’s focus</span>
              {priorities.length > 0 ? (
                priorities.map((p, i) => (
                  <div key={i} className={`flex items-center gap-space-sm px-space-md py-space-sm rounded-full border shadow-sm ${i === 0 ? 'bg-primary-container border-outline-variant/40' : 'bg-surface-container border-surface-container-high'}`}>
                    <span className={`w-2 h-2 rounded-full ${i === 0 ? 'bg-surface-tint' : 'bg-secondary'}`} />
                    <span className="font-label-md text-label-md uppercase tracking-wide text-outline font-semibold">Priority {p.rank || i + 1}: {p.label || p.foundation_code}</span>
                  </div>
                ))
              ) : (
                <div className="flex items-center gap-space-sm px-space-md py-space-sm rounded-full bg-surface-container border border-surface-container-high">
                  <span className="w-2 h-2 rounded-full bg-secondary" />
                  <span className="font-label-md text-label-md uppercase tracking-wide text-on-surface-variant">No priorities set yet</span>
                </div>
              )}
            </div>
            <div className="mt-space-md lg:mt-space-lg pt-space-md border-t border-surface-container w-full lg:max-w-xs">
              {!editingPriorities ? (
                <button data-testid="edit-priorities-button" onClick={() => setEditingPriorities(true)} className="w-full flex items-center justify-between font-label-md text-label-md uppercase font-bold text-outline hover:text-surface-tint transition-colors">
                  <span>Set weekly priorities</span>
                  <Icon name="tune" className="text-[18px]" />
                </button>
              ) : (
                <div data-testid="priorities-editor" className="flex flex-col gap-space-sm">
                  <span className="font-label-sm text-label-sm uppercase tracking-wider text-on-surface-variant">Pick up to 3 focuses</span>
                  <div className="flex flex-wrap gap-1.5 max-h-40 overflow-y-auto">
                    {fprogress.map((f) => {
                      const on = prioritySel.includes(f.code);
                      return (
                        <button key={f.code} data-testid={`priority-option-${f.code}`} onClick={() => togglePriority(f.code)} className={`px-2 py-1 rounded-full font-label-sm text-label-sm uppercase tracking-wide transition-colors ${on ? 'bg-surface-tint text-on-primary' : 'bg-surface-container text-on-surface-variant hover:bg-surface-container-high'}`}>
                          {f.name}
                        </button>
                      );
                    })}
                  </div>
                  <div className="flex items-center gap-space-sm pt-space-xs">
                    <button data-testid="save-priorities-button" onClick={savePriorities} className="h-9 px-space-md rounded bg-outline text-on-primary hover:bg-surface-tint font-label-md text-label-md uppercase tracking-wider font-semibold transition-colors">Save</button>
                    <button onClick={() => { setEditingPriorities(false); setPrioritySel(priorities.map((it) => it.foundation_code)); }} className="h-9 px-space-md rounded bg-surface-container text-outline font-label-md text-label-md uppercase tracking-wider transition-colors">Cancel</button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Sections 1-6: wellbeing snapshot, priorities, today's action, challenge, reading */}
      <WellbeingSpaceTop
        name={firstName}
        habits={habits}
        progressByHabit={progressByHabit}
        logHabit={logHabit}
        streak={streak}
        onHabitsChanged={loadDashboard}
      />

      {/* Streak + achievement */}
      {(() => {
        const completed = fprogress.filter((f) => f.total > 0 && f.explored === f.total);
        const nextSuggest = fprogress.find((f) => !(f.total > 0 && f.explored === f.total));
        const cur = streak ? streak.current_streak : 0;
        if (cur < 1 && completed.length === 0) return null;
        return (
          <section className="w-full px-margin-mobile lg:px-margin pb-space-lg">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-gutter">
              {cur >= 1 && (
                <div data-testid="dashboard-streak" className="p-space-lg rounded-xl bg-surface-container-low border border-surface-container flex items-center gap-space-md shadow-sm">
                  <span className="w-11 h-11 rounded-full bg-primary-container text-surface-tint flex items-center justify-center shrink-0"><Icon name="eco" className="text-[24px]" /></span>
                  <div>
                    <p className="font-title-md text-title-md text-outline font-bold">{cur === 1 ? 'You showed up today' : `${cur} days in a row`}</p>
                    <p className="font-body-sm text-body-sm text-on-surface-variant">{cur >= 3 ? 'Lovely, gentle consistency — small steps really do add up. No pressure to be perfect.' : 'A calm, steady start. Come back tomorrow whenever it suits you.'}</p>
                  </div>
                </div>
              )}
              {completed.length > 0 && (
                <div data-testid="dashboard-achievement" className="p-space-lg rounded-xl bg-outline text-surface flex items-center gap-space-md shadow-md relative overflow-hidden">
                  <div className="absolute -right-6 -bottom-6 w-28 h-28 rounded-full bg-surface-tint/20 blur-2xl pointer-events-none" />
                  <span className="w-11 h-11 rounded-full bg-surface-tint text-on-primary flex items-center justify-center shrink-0"><Icon name="celebration" className="text-[24px]" /></span>
                  <div className="relative z-10">
                    <p className="font-title-md text-title-md text-surface font-bold">You’ve explored all of {completed[0].name}</p>
                    <p className="font-body-sm text-body-sm text-surface-container-high">Beautifully done. {nextSuggest ? (<>Whenever you feel ready, <Link to={`/essentials/${nextSuggest.slug}`} className="text-surface-tint font-semibold hover:underline">explore {nextSuggest.name}</Link>.</>) : 'You’ve explored every Essential — wonderful.'}</p>
                  </div>
                </div>
              )}
            </div>
          </section>
        );
      })()}

      {/* Hero recommendation */}
      <section className="w-full px-margin-mobile lg:px-margin pb-space-2xl">
        <div className="w-full bg-primary-container rounded-xl p-space-lg lg:p-space-xl relative overflow-hidden shadow-sm border border-outline-variant/30">
          <div className="absolute top-0 left-0 bottom-0 w-2 bg-surface-tint shadow-[0_0_16px_rgba(240,40,223,0.4)]" />
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-gutter items-center">
            <div className="lg:col-span-8 flex flex-col pl-space-xs lg:pl-space-sm">
              <div className="flex flex-wrap items-center gap-space-sm mb-space-sm">
                <Icon name="wb_twilight" className="text-surface-tint text-[20px]" />
                <span className="font-label-md text-label-md uppercase tracking-widest text-primary font-bold">Your Next Small Step</span>
                <span className="font-label-sm text-label-sm uppercase px-space-xs py-0.5 rounded bg-surface text-on-surface-variant">Right Now</span>
              </div>
              <h2 className="font-headline-lg text-headline-lg tracking-tight text-outline mb-space-sm" data-testid="dashboard-next-step">{nextHabit ? nextHabit.title : (savedTopics[0] ? savedTopics[0].title : 'Explore your first topic')}</h2>
              <p className="font-body-md text-body-md text-on-surface-variant max-w-3xl leading-relaxed mb-space-lg">
                {nextHabit
                  ? `A gentle step you added${nextHabit.source_topic_title ? ` from “${nextHabit.source_topic_title}”` : ''}. Little and often beats big and rare — mark it done once you’ve done it today.`
                  : savedTopics[0]
                  ? `You saved “${savedTopics[0].title}”. Open it again to pick up where you left off.`
                  : 'Open any topic in the library to save it and add a recommended habit — your personal next steps will appear here.'}
              </p>
              <div className="flex flex-wrap items-center gap-space-md">
                {nextHabit ? (
                  <button data-testid="dashboard-next-step-log" onClick={() => logHabit(nextHabit.id)} disabled={nextHabitDone} className={`h-12 px-space-lg rounded-lg font-label-md text-label-md uppercase tracking-wider font-semibold shadow-[0_4px_16px_rgba(240,40,223,0.3)] transition-all flex items-center gap-space-xs group ${nextHabitDone ? 'bg-outline text-on-primary' : 'bg-surface-tint text-on-primary hover:bg-primary'}`}>
                    <Icon name={nextHabitDone ? 'done' : 'check_circle'} className="text-[18px] group-hover:scale-110 transition-transform" />
                    <span>{nextHabitDone ? 'Completed Today ✓' : 'Mark Completed'}</span>
                  </button>
                ) : (
                  <Link to="/resources" className="h-12 px-space-lg rounded-lg bg-surface-tint text-on-primary font-label-md text-label-md uppercase tracking-wider font-semibold shadow-[0_4px_16px_rgba(240,40,223,0.3)] hover:bg-primary transition-all flex items-center gap-space-xs">
                    <Icon name="menu_book" className="text-[18px]" /><span>Browse the Library</span>
                  </Link>
                )}
                {nextHabit && nextHabit.source_topic_slug && (
                  <Link className="font-label-md text-label-md uppercase tracking-wider text-outline hover:text-surface-tint transition-colors flex items-center gap-1" to={`/topics/${nextHabit.source_topic_slug}`}>
                    Read the topic <span className="text-surface-tint">→</span>
                  </Link>
                )}
              </div>
            </div>
            <div className="lg:col-span-4 relative mt-space-lg lg:mt-0">
              <div className="w-full aspect-[4/3] rounded-lg overflow-hidden relative shadow-md bg-surface">
                <div className="w-full h-full bg-cover bg-center" style={{ backgroundImage: `url('${HERO_IMG}')` }} />
                <div className="absolute inset-0 bg-gradient-to-t from-outline/60 via-transparent to-transparent flex flex-col justify-end p-space-md text-on-primary">
                  <span className="font-label-sm text-label-sm uppercase tracking-widest text-primary-fixed">Your Calm Anchor</span>
                  <span className="font-title-md text-title-md font-semibold">One gentle step at a time</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <WellbeingProfile />

      {/* Active Daily Rhythm */}      <section className="w-full px-margin-mobile lg:px-margin pb-space-2xl">
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-space-lg gap-space-sm">
          <div>
            <span className="font-label-md text-label-md uppercase tracking-widest text-primary font-bold">Practical Groundwork</span>
            <h2 className="font-headline-md text-headline-md tracking-tight text-outline">Active Daily Rhythm</h2>
          </div>
          <div className="max-w-md">
            <p className="font-body-sm text-body-sm text-on-surface-variant italic">“Remember: A missed day is simply data, not failure. Consistency over intensity.”</p>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-gutter" data-testid="dashboard-habits">
          {habits.length === 0 && (
            <div className="md:col-span-3 p-space-lg rounded-xl bg-surface-container-lowest border border-surface-container text-center" data-testid="dashboard-habits-empty">
              <p className="font-body-md text-body-md text-on-surface-variant">No habits yet. Open any <Link to="/resources" className="text-surface-tint font-semibold">topic in the library</Link> and tap “Add” on a recommended habit to start tracking it here.</p>
            </div>
          )}
          {habits.map((h, i) => {
            const done = progressByHabit[h.id];
            return (
              <div key={h.id} data-testid={`dashboard-habit-${h.id}`} className="p-space-lg rounded-xl bg-surface-container-lowest border border-surface-container flex flex-col justify-between hover:border-surface-tint/40 transition-colors shadow-sm">
                <div>
                  <div className="flex items-center justify-between mb-space-md">
                    <span className="font-label-sm text-label-sm uppercase tracking-widest text-on-surface-variant font-bold">Habit {String(i + 1).padStart(2, '0')}</span>
                    <div className="flex items-center gap-space-sm">
                      <span className={`px-space-sm py-1 rounded-full font-label-sm text-label-sm uppercase tracking-wide font-semibold ${done ? 'bg-surface text-secondary flex items-center gap-1' : 'bg-primary-container text-surface-tint'}`}>{done ? (<><Icon name="check" className="text-[14px]" /> Done today</>) : 'To do'}</span>
                      <button data-testid={`habit-remove-${h.id}`} onClick={() => removeHabit(h.id)} aria-label="Remove habit" title="Remove habit" className="text-on-surface-variant hover:text-error transition-colors"><Icon name="close" className="text-[18px]" /></button>
                    </div>
                  </div>
                  <h3 className={`font-title-md text-title-md text-outline mb-space-xs ${done ? 'line-through opacity-80' : ''}`}>{h.title}</h3>
                  {h.source_topic_title && (
                    <Link to={`/topics/${h.source_topic_slug}`} className="font-body-sm text-body-sm text-on-surface-variant hover:text-surface-tint transition-colors">From: {h.source_topic_title}</Link>
                  )}
                </div>
                <div className="pt-space-md mt-space-md border-t border-surface-container flex items-center justify-between">
                  <span className="font-label-sm text-label-sm text-on-surface-variant uppercase">{h.cadence || 'daily'}</span>
                  <button
                    data-testid={`habit-log-${h.id}`}
                    onClick={() => logHabit(h.id)}
                    disabled={done}
                    className={`px-space-md py-1.5 rounded font-label-md text-label-md uppercase tracking-wider transition-all ${done ? 'bg-surface-tint text-on-primary' : 'bg-surface-container text-outline hover:bg-surface-tint hover:text-on-primary'}`}
                  >
                    {done ? 'Completed ✓' : 'Log Done'}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Active Foundations */}
      <section className="w-full px-margin-mobile lg:px-margin pb-space-2xl">
        <div className="p-space-lg lg:p-space-xl rounded-xl bg-surface-container-low border border-surface-container">
          <div className="flex flex-col md:flex-row md:items-center justify-between pb-space-lg border-b border-surface-container gap-space-md">
            <div>
              <span className="font-label-md text-label-md uppercase tracking-widest text-primary font-bold">Living Systems</span>
              <h2 className="font-headline-md text-headline-md tracking-tight text-outline">Active Essentials Engagement</h2>
            </div>
            <Link to="/longevity" className="font-label-md text-label-md uppercase tracking-wider text-outline hover:text-surface-tint transition-colors">View all the Essentials →</Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-gutter pt-space-lg" data-testid="dashboard-foundation-progress">
            {fprogress.map((f) => {
              const pct = f.total > 0 ? Math.round((f.explored / f.total) * 100) : 0;
              return (
                <Link key={f.code} to={`/essentials/${f.slug}`} data-testid={`fprogress-${f.code}`} className="p-space-md rounded-lg bg-surface border-l-2 border-surface-tint/60 hover:border-surface-tint flex flex-col justify-between shadow-sm transition-colors group">
                  <div>
                    <div className="flex items-center justify-between mb-space-xs">
                      <span className="font-label-sm text-label-sm uppercase tracking-widest font-bold text-primary">Essential {String(f.order).padStart(2, '0')}</span>
                      <span className="w-2 h-2 rounded-full bg-surface-tint" />
                    </div>
                    <h3 className="font-title-md text-title-md text-outline group-hover:text-surface-tint transition-colors">{f.name}</h3>
                  </div>
                  <div className="mt-space-md">
                    <div className="w-full h-1.5 rounded-full bg-surface-container overflow-hidden mb-space-xs">
                      <div className="h-full bg-surface-tint transition-all" style={{ width: `${pct}%` }} />
                    </div>
                    <div className="flex items-center justify-between font-label-sm text-label-sm uppercase text-on-surface-variant">
                      <span>Topics explored</span>
                      <span className="font-bold text-outline">{f.explored} / {f.total}</span>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* Reflection */}
      <section className="w-full px-margin-mobile lg:px-margin pb-space-2xl">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-gutter items-stretch">
          <div className="lg:col-span-5 flex flex-col justify-between bg-outline p-space-xl rounded-xl text-on-primary">
            <div>
              <span className="font-label-md text-label-md uppercase tracking-widest text-primary-fixed font-semibold mb-space-xs block">Weekly Self-Inquiry</span>
              <h2 className="font-headline-sm text-headline-sm uppercase tracking-wide text-on-primary mb-space-md">The Saturday Compass</h2>
              <blockquote className="font-headline-sm text-headline-sm italic text-primary-fixed-dim leading-snug">“When in the past 7 days did you feel your energy was most steady, and what preceded that moment?”</blockquote>
            </div>
            <div className="mt-space-xl pt-space-md border-t border-surface-container-highest/20 font-body-sm text-body-sm text-surface-container-highest">Reflections are private to your account • Never analysed by algorithms</div>
          </div>
          <div className="lg:col-span-7 bg-surface-container-lowest p-space-xl rounded-xl border border-surface-container flex flex-col justify-between shadow-sm">
            <div className="flex flex-col h-full">
              <label className="font-label-md text-label-md uppercase tracking-wider text-outline mb-space-sm font-semibold flex items-center justify-between" htmlFor="reflectionInput">
                <span>Your Personal Observations</span>
                <span className="text-on-surface-variant font-normal">{reflection ? `${reflection.length} chars` : 'Unsaved Draft'}</span>
              </label>
              <textarea
                id="reflectionInput"
                data-testid="reflection-input"
                value={reflection}
                onChange={(e) => { setReflection(e.target.value); setSaved(false); }}
                className="w-full p-space-md bg-surface-container-low rounded-lg border border-surface-container font-body-md text-body-md text-outline focus:outline-none focus:border-surface-tint focus:ring-1 focus:ring-surface-tint transition-all resize-none placeholder:text-on-surface-variant/60"
                placeholder="Write freely. Notice patterns around meals, quiet mornings, or social nourishment..."
                rows={6}
              />
            </div>
            <div className="mt-space-md flex items-center justify-between pt-space-md border-t border-surface-container">
              <span className="font-body-sm text-body-sm text-on-surface-variant" data-testid="reflection-status">{saved ? 'Saved just now' : formatSaved(lastSaved)}</span>
              <button onClick={saveReflection} disabled={savingReflection} data-testid="reflection-save-button" className="h-10 px-space-lg rounded-lg bg-outline text-on-primary hover:bg-surface-tint transition-colors font-label-md text-label-md uppercase tracking-wider font-semibold disabled:opacity-60">{savingReflection ? 'Saving…' : 'Save Reflection'}</button>
            </div>
          </div>
        </div>
      </section>

      {/* Saved topics library */}
      <section className="w-full px-margin-mobile lg:px-margin pb-space-2xl" id="saved-library">
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-space-lg">
          <div>
            <span className="font-label-md text-label-md uppercase tracking-widest text-primary font-bold">Your Library</span>
            <h2 className="font-headline-md text-headline-md tracking-tight text-outline">Saved Topics</h2>
          </div>
          <Link to="/resources" className="font-label-md text-label-md uppercase tracking-wider text-on-surface-variant hover:text-surface-tint transition-colors">Browse the Library →</Link>
        </div>
        {savedTopics.length === 0 ? (
          <div className="p-space-lg rounded-xl bg-surface-container-lowest border border-surface-container text-center" data-testid="dashboard-saved-empty">
            <p className="font-body-md text-body-md text-on-surface-variant">You haven’t saved any topics yet. Open a <Link to="/resources" className="text-surface-tint font-semibold">topic in the library</Link> and tap “Save this topic” to keep it here.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-gutter" data-testid="dashboard-saved">
            {savedTopics.map((s) => (
              <div key={s.id} data-testid={`saved-topic-${s.content_id}`} className="group p-space-lg rounded-xl bg-surface-container-lowest border border-surface-container hover:border-surface-tint/40 transition-all flex items-center justify-between gap-space-md shadow-sm">
                <Link to={`/topics/${s.slug || ''}`} className="flex-1 min-w-0">
                  <div className="flex items-center gap-space-xs font-label-sm text-label-sm uppercase text-primary font-bold mb-space-xs">
                    <Icon name="bookmark" className="text-[14px]" /> <span>{s.content_id}</span>{s.domain ? <> • <span className="text-on-surface-variant">{s.domain}</span></> : null}
                  </div>
                  <h3 className="font-headline-sm text-headline-sm text-outline group-hover:text-primary transition-colors">{s.title || s.content_id}</h3>
                </Link>
                <button data-testid={`saved-remove-${s.content_id}`} onClick={() => removeSaved(s.id)} aria-label="Remove saved topic" title="Remove" className="shrink-0 text-on-surface-variant hover:text-error transition-colors"><Icon name="close" className="text-[20px]" /></button>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Quick action bar */}
      <section className="w-full px-margin-mobile lg:px-margin pb-space-3xl">
        <div className="w-full py-space-lg px-space-xl rounded-xl bg-surface-container flex flex-wrap items-center justify-between gap-space-md border border-surface-container-high">
          <div className="flex items-center gap-space-md">
            <Icon name="explore" className="text-outline text-[24px]" />
            <span className="font-title-md text-title-md text-outline">Keep Building Your Practice</span>
          </div>
          <div className="flex flex-wrap items-center gap-space-sm">
            <Link to="/longevity" className="h-10 px-space-md rounded bg-surface text-outline hover:text-surface-tint border border-surface-container-high font-label-md text-label-md uppercase tracking-wider font-semibold transition-colors flex items-center">Start with the Essentials</Link>
            <Link to="/wellbeing" className="h-10 px-space-md rounded bg-surface text-outline hover:text-surface-tint border border-surface-container-high font-label-md text-label-md uppercase tracking-wider font-semibold transition-colors flex items-center">Explore Areas of Life</Link>
            <Link to="/resources" className="h-10 px-space-md rounded bg-outline text-on-primary hover:bg-surface-tint font-label-md text-label-md uppercase tracking-wider font-semibold transition-colors flex items-center">Open the Library</Link>
          </div>
        </div>
      </section>
      <AccountSettings />
    </div>
  );
}
